import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
} from '@angular/forms';
import {NgTemplateOutlet} from '@angular/common';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {v4 as uuidv4} from 'uuid';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';
import {trimIfString} from '@brunata-angular/util';

@Component({
    selector: 'app-input-text-area-group',
    templateUrl: './input-text-area-group.component.html',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        NgTemplateOutlet,
        ControlMessagesComponent,
        PopoverIconComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTextAreaGroupComponent implements OnInit, ControlValueAccessor {
    @Input() label = '';
    /**
     * When set, renders the Bootstrap grid layout (label in a `col-{labelCols}`, field in the
     * remaining columns) instead of the default stacked layout. ngwebbill passes `4` to match its
     * legacy `app-input-textarea-group`; other apps leave it unset for the stacked layout.
     */
    @Input() labelCols: number | null = null;
    @Input() maxLength: number | null = null;
    @Input() required = false;
    @Input() rows = 4;
    /** Trim the value on change (ngwebbill's legacy behaviour via `StringPipe`). Off by default. */
    @Input() trim = false;
    @Input() information: string | undefined;

    form = new FormControl<string>('');
    id: string = uuidv4();
    onTouch: (() => void) | undefined;

    private onChange: ((value: string | null) => void) | undefined;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        ngControl.valueAccessor = this;
    }

    get labelClass(): string {
        const cols = this.labelCols != null ? `col-${this.labelCols} ` : '';
        return `${cols}${this.required ? 'required' : ''}`.trim();
    }

    get inputColsClass(): string {
        return this.labelCols != null ? `col-${12 - this.labelCols}` : '';
    }

    ngOnInit(): void {
        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const changeDetectorRef = this.changeDetectorRef;
            this.ngControl.control.markAsTouched = function (...args): void {
                originalMarkAsTouched.apply(this, args);
                changeDetectorRef.detectChanges();
            };
        }

        this.form.valueChanges.subscribe(data => {
            if (this.onChange == null) {
                return;
            }
            this.onChange(this.trim ? trimIfString(data) : data);
        });
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: string): void {
        this.form.setValue(value, {emitEvent: false});
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }
}
